// Plan 15-07 Task 2 — ShareMyCodeSheet (DEC-002 — Copy / Share-link / Show-QR).
//
// Bottom-sheet (Pattern S1 from EmpowerBottomSheet). All three add-a-friend
// share actions are visible at once — no nested navigation. Per UI-SPEC, the
// Show-QR action toggles an inline 200×200 QRCanvas beneath the row.
//
//   - Copy code: writes friend-code to clipboard → dispatches
//     `runegambit:toast` (id `social.friends.share.copy_code_done`).
//   - Share link: feature-detects navigator.share; falls back to clipboard
//     with the same toast on absent.
//   - Show QR: toggles a useState boolean; renders <QRCanvas> when true.
//
// Threat model — T-15-33 (Information disclosure): the friend code IS personal
// but sharing it is the player's choice. The sheet renders the code only when
// the user opens it (own profile / own trigger).

import { type JSX, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Sheet } from '../components/Sheet'
import { QRCanvas } from './QRCanvas.js'

export interface ShareMyCodeSheetProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly friendCode: string
  readonly shareUrl: string
}

function dispatchCopyToast(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('runegambit:toast', {
      detail: { id: 'social.friends.share.copy_code_done' },
    }),
  )
}

export function ShareMyCodeSheet({
  open,
  onClose,
  friendCode,
  shareUrl,
}: ShareMyCodeSheetProps): JSX.Element | null {
  const intl = useIntl()
  const [qrOpen, setQrOpen] = useState(false)

  const handleCopyCode = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(friendCode)
      dispatchCopyToast()
    } catch {
      // Permission denied or clipboard unsupported — silently no-op; the
      // user can copy the visible code manually.
    }
  }, [friendCode])

  const handleShareLink = useCallback(async () => {
    if (typeof navigator === 'undefined') return
    // biome-ignore lint/suspicious/noExplicitAny: navigator.share may be undefined
    const nav = navigator as any
    if (typeof nav.share === 'function') {
      try {
        await nav.share({
          title: intl.formatMessage({
            id: 'social.friends.share.title',
            defaultMessage: 'Share your code',
          }),
          url: shareUrl,
        })
        return
      } catch {
        // User cancelled or share failed → fall through to clipboard copy.
      }
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl)
        dispatchCopyToast()
      } catch {
        // No-op on permission denial.
      }
    }
  }, [intl, shareUrl])

  const handleShowQR = useCallback(() => {
    setQrOpen((prev) => !prev)
  }, [])

  // Reset QR-toggle when the sheet closes so the next open starts collapsed.
  useEffect(() => {
    if (!open) setQrOpen(false)
  }, [open])

  if (!open) return null

  return (
    // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
    // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim.
    <Sheet
      onClose={onClose}
      testId="share-my-code-sheet"
      labelledBy="share-my-code-title"
      panelStyle={{ overflow: 'hidden' }}
    >
      <div>
        <div
          style={{
            height: '48px',
            backgroundColor: 'var(--color-paper-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderBottom:
              '1px solid color-mix(in srgb, var(--color-surface-stroke) 30%, transparent)',
          }}
        >
          <h2
            id="share-my-code-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-18)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.friends.share.title"
              defaultMessage="Share your code"
            />
          </h2>
        </div>
        <div
          style={{
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <p
            data-testid="share-my-code-display"
            style={{
              margin: 0,
              padding: '12px 16px',
              backgroundColor: 'var(--color-player-b-tint)',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-tile)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-20)',
              letterSpacing: '0.1em',
              textAlign: 'center',
              color: 'var(--color-text-primary)',
            }}
          >
            {friendCode}
          </p>
          <button
            type="button"
            data-cta="share-copy-code"
            onClick={() => {
              void handleCopyCode()
            }}
            style={{
              height: '52px',
              backgroundColor: 'var(--color-cta-primary)',
              color: 'var(--color-inkwell)',
              border: 'none',
              boxShadow: '0 4px 0 var(--color-cta-primary-deep)',
              borderRadius: '12px',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage
              id="social.friends.share.copy_code_cta"
              defaultMessage="Copy code"
            />
          </button>
          <button
            type="button"
            data-cta="share-share-link"
            onClick={() => {
              void handleShareLink()
            }}
            style={{
              height: '52px',
              backgroundColor: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              border: 'none',
              boxShadow:
                'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 40%, transparent), var(--shadow-card)',
              borderRadius: '12px',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage
              id="social.friends.share.share_link_cta"
              defaultMessage="Share link"
            />
          </button>
          <button
            type="button"
            data-cta="share-show-qr"
            aria-expanded={qrOpen}
            aria-controls="share-my-code-qr-region"
            onClick={handleShowQR}
            style={{
              height: '52px',
              backgroundColor: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              border: 'none',
              boxShadow:
                'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 40%, transparent), var(--shadow-card)',
              borderRadius: '12px',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage
              id="social.friends.share.show_qr_cta"
              defaultMessage="Show QR"
            />
          </button>
          {qrOpen && (
            <div
              id="share-my-code-qr-region"
              data-testid="share-my-code-qr-region"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: 'var(--color-player-b-tint)',
                borderRadius: '12px',
                boxShadow: 'var(--shadow-tile)',
                minWidth: '240px',
                alignSelf: 'center',
              }}
            >
              <QRCanvas shareUrl={shareUrl} size={200} />
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-family-display)',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-14)',
                  color: 'var(--color-player-b-deep)',
                }}
              >
                <FormattedMessage
                  id="social.friends.share.qr_caption"
                  defaultMessage="Scan to add me"
                />
              </p>
            </div>
          )}
        </div>
      </div>
    </Sheet>
  )
}
